<template>
  <v-row>
    <v-col>
      <p v-if="label" class="font-weight-medium mb-1">{{ label }}</p>
      <v-item-group
        :id="id"
        :value="value"
        :multiple="multiple"
        :mandatory="mandatory"
        :data-test="resolvedDataTest"
      >
        <v-item
          v-for="(item, index) in items"
          :key="index"
          :value="item[itemKey]"
        >
          <template #default="{ active }">
            <!-- Per-chip id suffixed off the group's resolved id, so a test can pick one chip by its technical key, not its translated label. -->
            <v-chip
              class="ml-0 mr-1"
              :data-test="resolvedDataTest ? `${resolvedDataTest}-${item[itemKey]}` : undefined"
              :style="{backgroundColor: chipBackgroundColor(active, item), borderColor: chipBackgroundColor(active, item)}"
              :text-color="chipColor(active, item)"
              @click="onClick(item[itemKey])"
            >
              <v-icon v-if="item[itemIcon]" small class="mr-1 text-inherit!">{{ item[itemIcon] }}</v-icon>
              {{ item[itemName] }}
              <div
                v-if="Number.isInteger(item[itemCount])"
                class="ml-1 w-5 rounded-[100%] text-center"
                :class="active
                  ? 'bg-primary-text text-primary-onBase-highEmphasis font-weight-medium'
                  : 'bg-core-surface in-[.theme--dark]:bg-core-border'"
              >
                {{ item[itemCount] }}
              </div>
            </v-chip>
          </template>
        </v-item>
      </v-item-group>
    </v-col>
    <v-col v-if="hasError" cols="12">
      <span class="caption error--text">Pole wymagane</span>
    </v-col>
  </v-row>
</template>

<script setup lang="ts">
import {computed, getCurrentInstance, inject, onUnmounted, ref, watch} from 'vue';
import {Utils} from '@/services/utils/BasicUtils';
import {UserLocalStorage} from '@/services/UserLocalStorage';
import {resolveDataTest} from '@/components/fpComponents/useDataTest';

interface FpChipRadioPropsValidation {
  readonly required: boolean;
}

type FpChipRadioItem = Record<string, any>;

interface FpChipRadioProps {
  // Must stay `unknown` - a narrower type emits a runtime type-check and Vue warns on the `null` emitted on deselect.
  value: unknown;
  items?: FpChipRadioItem[];
  required?: boolean;
  multiple?: boolean;
  label?: string;
  itemKey?: string;
  itemName?: string;
  itemIcon?: string;
  itemCount?: string;
  prependIcon?: string;
  itemColor?: string;
  localStorageKey?: string;
  id?: string;
  dataTest?: string;
  mandatory?: boolean;
}

const props = withDefaults(defineProps<FpChipRadioProps>(), {
  items: () => ([
    {
      type: true,
      name: 'Tak',
    },
    {
      type: false,
      name: 'Nie',
    },
  ]),
  required: false,
  multiple: false,
  itemKey: 'type',
  itemName: 'name',
  itemIcon: 'icon',
  itemCount: 'count',
  itemColor: 'color',
  mandatory: false,
});

const emit = defineEmits<{
  (e: 'update:value', value: any): void;
  (e: 'input', value: any): void;
}>();

const instance = getCurrentInstance()!;
const form = inject<Nullable<IForm>>('form', null);

const resolvedDataTest = computed<string | undefined>(() => resolveDataTest(props.dataTest, props.id));

const hasError = ref<boolean>(false);
const rules: ValidationRules<FpChipRadioPropsValidation, unknown> = {
  required: (v) => Array.isArray(v) ? v.length > 0 : !!v,
};

const resetValidation = (): void => {
  hasError.value = false;
};

const validate = (): boolean => {
  hasError.value = Utils.keys<FpChipRadioPropsValidation>(rules)
    .map((key) => (props[key] === false || props[key] === undefined) || rules[key]!(props.value))
    .includes(false);
  return !hasError.value;
};

const chipColor = (active: boolean, item: FpChipRadioItem): string => hasError.value
  ? 'var(--token-error-base)'
  : active
    ? item[props.itemColor] || 'var(--token-primary-text)'
    : '';

// `color(from …)` so the tint also works for `var(--token-…)` item colours, which a
// hex-appended alpha silently breaks.
const chipBackgroundColor = (active: boolean, item: FpChipRadioItem): string => hasError.value
  ? 'var(--token-error-base)'
  : active
    ? `color(from ${item[props.itemColor] || 'var(--token-primary-text)'} srgb r g b / 0.21)`
    : '';

const updateSelectedTags = (value: unknown): void => {
  if (!props.multiple && !props.mandatory && value === props.value) {
    value = null;
  }
  if (props.value !== value) {
    // Order matters: .sync must write the model before the @input listeners run.
    emit('update:value', value);
    emit('input', value);
  }
  if (props.localStorageKey) {
    UserLocalStorage.setItem(props.localStorageKey, typeof value === 'object' ? JSON.stringify(value) : value as string);
  }
};

const onClick = (tag?: unknown): void => {
  resetValidation();
  if (tag !== undefined) {
    if (props.multiple) {
      if (Array.isArray(props.value)) {
        if (!props.value.includes(tag)) {
          props.value.push(tag);
          updateSelectedTags(props.value);
        } else {
          updateSelectedTags(props.value.filter(x => x !== tag));
        }
      } else {
        updateSelectedTags([tag,]);
      }
    } else {
      updateSelectedTags(tag);
    }
  } else {
    console.error('Tag is invalid', tag);
    if (props.items[0] && !props.items[0][props.itemKey]) {
      console.error('itemKey is invalid', props.itemKey);
    }
  }
};

watch(() => props.required, () => {
  resetValidation();
});

defineExpose({form, validate, resetValidation,});

if (form) {
  form.register(instance.proxy);
}

onUnmounted(() => {
  if (form) {
    form.unregister(instance.proxy);
  }
});
</script>
